import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { useQuiz } from "../context/QuizContext";
import authService from "../services/authService";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/data-display";

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUserInfo, setFlowStage } = useQuiz();
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("Verifying your organisation account…");
  const processingRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (processingRef.current) return;
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");
      if (!code || !state) {
        setStatus("error");
        setMessage(error ? `Authentication failed: ${errorDescription || error}` : "No authorization code was received.");
        setTimeout(() => navigate("/"), 3000);
        return;
      }
      processingRef.current = true;
      try {
        setMessage("Completing secure sign-in…");
        const authResult = await authService.handleCallback(code, state);
        setUserInfo({ email: authResult.user_email, name: authResult.user_name });
        setFlowStage("authenticated");
        setStatus("success");
        setMessage("You’re signed in. Preparing your learning space…");
        setTimeout(() => navigate("/education"), 1500);
      } catch {
        setStatus("error");
        setMessage("Authentication failed. Please try again.");
        setTimeout(() => navigate("/"), 3000);
      }
    };
    handleCallback();
  }, [navigate, searchParams, setFlowStage, setUserInfo]);

  const Icon = status === "success" ? CheckCircle2 : status === "error" ? XCircle : Loader2;
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10">
      <Card className="w-full max-w-md border-white/10 bg-slate-900 text-white shadow-float">
        <CardContent className="p-8 text-center">
          <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-md bg-sky-500/15 text-sky-400"><Icon className={`h-6 w-6 ${status === "processing" ? "animate-spin" : ""}`} /></div>
          <div className="mb-6 flex items-center justify-center gap-2 text-sm font-semibold text-slate-300"><ShieldCheck className="h-4 w-4 text-sky-400" />PhishGuard secure sign-in</div>
          <h1 className="text-xl font-bold">{status === "processing" ? "Authenticating" : status === "success" ? "Welcome back" : "Sign-in unsuccessful"}</h1>
          <p className="mt-2 text-sm leading-6 text-slate-400">{message}</p>
          {status === "processing" && <Progress value={65} className="mt-6 [&>div]:bg-slate-800" />}
        </CardContent>
      </Card>
    </main>
  );
};

export default AuthCallback;
